import { isPosBetaCompany } from '@/util/posBeta';
import { isCampaignsBetaCompany } from '@/util/campaignsBeta';

// Maps wizard task feature_urls to the permission a user needs in order to
// navigate to the destination page. The set mirrors the sidebar guards in
// DashboardLayout.vue, so a wizard task ends up locked for the user only when
// the destination page itself would be locked.
//
// Matching uses LONGEST-PREFIX wins, so '/games/tracker/settings' beats
// '/games' for a task that points at the tracker settings page. Tasks whose
// feature_url is missing or whose prefix isn't listed are treated as
// permissionless (visible and actionable for everyone). That's the safe
// default for tasks like personal profile setup that any logged-in user can
// complete.

// Ordered most-specific first within each domain.
const FEATURE_URL_PERMISSIONS = [
  // Personal profile — everyone
  ['/settings/profile', null],

  // Company / group settings
  ['/settings/company', 'company_settings_configure'],
  ['/settings/group', 'company_group_settings_configure'],
  ['/settings/billing', 'billing_view'],
  ['/settings/user-invite', 'company_users_invite'],
  ['/settings/user-management', 'company_users_view'],
  ['/settings/roles', 'roles_update'],
  ['/settings/custom-fields', 'custom_fields_configure'],
  ['/settings/migration', 'migrations_view'],
  ['/settings/logs', 'logs_view'],

  // Events (a.k.a. games in URL space)
  ['/games/tracker/settings', 'game_tracker_configure'],
  ['/games/tracker/select', 'game_tracker_submit'],
  ['/games/tracker', 'game_tracker_submit'],
  ['/games/clues/configure', 'games_configure'],
  ['/games/clues', 'hint_log_view'],
  ['/games/history', 'game_history_view'],
  ['/games/edit', 'games_configure'],
  ['/games/settings', 'games_configure'],
  ['/games', 'games_configure'],

  // Bookings
  ['/bookings/schedules', 'schedules_view'],
  ['/bookings/settings', 'booking_site_update'],
  ['/bookings/resource-calendar', 'bookings_view'],
  ['/bookings/calendar', 'bookings_view'],
  ['/bookings/kanban', 'bookings_view'],
  ['/bookings/list', 'bookings_view'],
  ['/bookings/tickets', 'bookings_view'],
  ['/bookings', 'bookings_view'],

  // Purchases
  ['/purchases/point-of-sale', 'payments_configure'],
  ['/purchases/settings', 'payments_configure'],
  ['/purchases/promos', 'promos_configure'],
  ['/purchases/gift-cards', 'gift_cards_view'],
  ['/purchases/inventory', 'inventory_view'],
  ['/purchases/transactions', 'transactions_view'],
  ['/purchases/payments', 'transactions_view'],
  ['/purchases/carts', 'carts_view'],

  // Marketing
  ['/marketing/campaigns', 'custom_emails_campaigns_configure'],

  // Checklists
  ['/checklists', 'checklists_view'],

  // Surveys
  ['/surveys', 'surveys_view'],

  // Photos
  ['/photos/settings', 'photo_settings_update'],
  ['/photos/new', 'photos_view'],
  ['/photos', 'photos_view'],

  // Mail
  ['/mail/custom/template-types', 'custom_emails_templates_configure'],
  ['/mail/custom/template', 'custom_emails_templates_configure'],
  ['/mail/custom/automation', 'custom_emails_automation_configure'],
  ['/mail/custom/landing', 'custom_emails_templates_configure'],
  ['/mail/custom', 'custom_emails_templates_configure'],
  ['/mail/automated', 'automated_emails_update'],
  ['/mail/settings', 'automated_emails_update'],
  ['/mail', 'automated_emails_update'],

  // SMS (top-level channel namespace; sms_configure swaps in at Phase 13)
  ['/sms', 'automated_emails_update'],

  // Customers
  ['/customers', 'customer_view'],

  // Waivers
  ['/waivers/types', 'waiver_settings_update'],
  ['/waivers/settings', 'waiver_settings_update'],
  ['/waivers/history', 'waivers_view'],
  ['/waivers', 'waivers_view'],

  // Dashboards / reports
  ['/dashboards/settings', 'dashboards_view'],
  ['/dashboards', 'dashboards_view'],
  ['/dashboard/game/leaderboard', 'leaderboards_view'],
  ['/dashboard/game/analytics', 'dashboards_view'],
  ['/dashboard', 'dashboards_view'],
  ['/reports', 'reports_view'],

  // Projects / time tracking
  ['/projects', 'project_view'],
  ['/time-tracker', 'time_log_view'],
];

export function getRequiredPermissionForTask(task) {
  if (!task || !task.feature_url) return null;
  const path = task.feature_url.split('?')[0];

  let best = null;
  let bestLen = -1;
  for (const [prefix, permission] of FEATURE_URL_PERMISSIONS) {
    if (path.startsWith(prefix) && prefix.length > bestLen) {
      best = permission;
      bestLen = prefix.length;
    }
  }
  return best;
}

function loadPermissions() {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '[]');
  } catch {
    return [];
  }
}

export function isTaskAllowed(task, permissions) {
  const required = getRequiredPermissionForTask(task);
  if (required === null) return true;
  const perms = permissions || loadPermissions();
  return perms.includes(required);
}

// Some tasks point at features that are behind a limited beta gated by a company
// allowlist rather than a permission. Those tasks are HIDDEN entirely (not just
// locked) for companies not in the beta, matching how the sidebar nav item and
// routes are hidden. A hidden task is dropped from its category and from the
// progress denominator so a non-beta company is never stuck below 100% on a task
// it can't see.
function isBetaHidden(task) {
  if (!task || !task.feature_url) return false;
  const path = task.feature_url.split('?')[0];
  // Point of Sale (src/util/posBeta.js)
  if (path.startsWith('/purchases/point-of-sale')) return !isPosBetaCompany();
  // Email campaigns (src/util/campaignsBeta.js)
  if (path.startsWith('/marketing/campaigns')) return !isCampaignsBetaCompany();
  return false;
}

// Recomputes overall progress by summing per-category counts. Use after
// markPermissionLocks so the denominator reflects the categories actually shown
// (beta-hidden tasks already dropped, permission-locked tasks still counted).
export function computeOverallProgress(categories) {
  let total = 0;
  let completed = 0;
  for (const category of categories || []) {
    total += category.total_tasks || 0;
    completed += category.completed_tasks || 0;
  }
  return {
    total_tasks: total,
    completed_tasks: completed,
    completion_percentage: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
}

// Returns the categories list with each task augmented with two fields:
//   permission_locked     -> true when the user lacks the required permission
//   required_permission   -> the permission name (for tooltip / debug)
// Categories themselves are NOT dropped — they always render so the user sees
// the full platform map and overall progress denominator stays consistent
// across roles. Locked tasks should be rendered as non-interactive in the UI.
export function markPermissionLocks(categories, permissions) {
  const perms = permissions || loadPermissions();
  return (categories || []).map(category => {
    const sourceTasks = category.tasks || [];
    // Drop beta-hidden tasks entirely before marking permission locks.
    const visibleTasks = sourceTasks.filter(t => !isBetaHidden(t));
    const tasks = visibleTasks.map(task => {
      const required = getRequiredPermissionForTask(task);
      return {
        ...task,
        required_permission: required,
        permission_locked: required !== null && !perms.includes(required),
      };
    });

    // If nothing was dropped, keep the API's category counts as-is (they still
    // include permission-locked tasks, which we intentionally count).
    if (visibleTasks.length === sourceTasks.length) {
      return { ...category, tasks };
    }

    // A beta-hidden task was removed; recompute this category's counts so the
    // denominator reflects only what the user can actually see.
    const completed = tasks.filter(t => t.is_completed).length;
    const total = tasks.length;
    return {
      ...category,
      tasks,
      total_tasks: total,
      completed_tasks: completed,
      completion_percentage: total === 0 ? 0 : Math.round((completed / total) * 100),
    };
  });
}
